/**
 * 画像処理ユーティリティ（クライアント Canvas）
 * - Saturation / コントラスト / ガンマ / ホワイトバランス（色温度）
 * - 全て Canvas + ピクセル直接操作
 * - 結果は Blob で返す（処理後に Storage 再アップロード）
 */

export interface ImageAdjustments {
  /** 彩度: -100〜100、0 が無調整 */
  saturation: number;
  /** コントラスト: -100〜100 */
  contrast: number;
  /** ガンマ: 0.2〜3.0、1.0 が無調整 */
  gamma: number;
  /** ホワイトバランス（色温度シフト）: -100（青寄り）〜100（赤寄り） */
  whiteBalance: number;
}

export const DEFAULT_ADJUSTMENTS: ImageAdjustments = {
  saturation: 0,
  contrast: 0,
  gamma: 1.0,
  whiteBalance: 0,
};

/** 画像 URL を <img> でロードする */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load image: ${url}`));
    img.src = url;
  });
}

/**
 * 画像に色調整を適用して Blob を返す
 */
export async function applyAdjustments(
  source: HTMLImageElement | HTMLCanvasElement,
  adj: ImageAdjustments,
): Promise<Blob> {
  const w = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
  const h = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');

  ctx.drawImage(source, 0, 0, w, h);
  const imgData = ctx.getImageData(0, 0, w, h);
  const data = imgData.data;

  const satFactor = 1 + adj.saturation / 100;
  const contrastFactor = 1 + adj.contrast / 100;
  const gamma = adj.gamma <= 0 ? 1 : adj.gamma;
  const wb = adj.whiteBalance / 100;
  const wbR = wb > 0 ? 1 + wb * 0.3 : 1;
  const wbB = wb < 0 ? 1 - wb * 0.3 : 1;
  const wbG = 1;

  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    // ホワイトバランス
    r = Math.min(255, r * wbR);
    g = Math.min(255, g * wbG);
    b = Math.min(255, b * wbB);

    // ガンマ
    r = 255 * Math.pow(r / 255, 1 / gamma);
    g = 255 * Math.pow(g / 255, 1 / gamma);
    b = 255 * Math.pow(b / 255, 1 / gamma);

    // コントラスト（中央 128 を軸）
    r = Math.max(0, Math.min(255, (r - 128) * contrastFactor + 128));
    g = Math.max(0, Math.min(255, (g - 128) * contrastFactor + 128));
    b = Math.max(0, Math.min(255, (b - 128) * contrastFactor + 128));

    // 彩度（HSL 変換せず、グレースケール混合で近似）
    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    r = Math.max(0, Math.min(255, gray + (r - gray) * satFactor));
    g = Math.max(0, Math.min(255, gray + (g - gray) * satFactor));
    b = Math.max(0, Math.min(255, gray + (b - gray) * satFactor));

    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
  }

  ctx.putImageData(imgData, 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      'image/png',
    );
  });
}
